import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, Square, Loader2, AlertCircle } from 'lucide-react'
import { useT } from '../../i18n/index.js'
import { ProcessingSteps, type ProcessingStep } from './ProcessingSteps.js'
import { UndoToast, type ExecutedAction } from './UndoToast.js'

// ── Types ──

type RecorderState = 'idle' | 'requesting-permission' | 'recording' | 'uploading' | 'processing' | 'done' | 'error'

interface VoiceRecorderFabProps {
  bienId?: string
  onComplete?: () => void
  /** Start recording immediately on mount (used by AssistantFab) */
  autoStart?: boolean
}

interface StartResponse {
  data: {
    voiceNoteId: string
    uploadUrl: string
    expiresIn: number
  }
}

interface ProcessEvent {
  step?: string
  status?: string
  detail?: string
  actionsExecuted?: ExecutedAction[]
  undoWindowExpiresAt?: string
  extractedEntities?: Record<string, unknown>
  error?: string
}

// ── Helpers ──

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const MAX_DURATION_SEC = 180

// ── Component ──

export function VoiceRecorderFab({ bienId, onComplete, autoStart }: VoiceRecorderFabProps) {
  const t = useT()
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedSec, setElapsedSec] = useState(0)
  const [errorMsg, setErrorMsg] = useState('')
  const [steps, setSteps] = useState<ProcessingStep[]>([])
  const [undoData, setUndoData] = useState<{
    voiceNoteId: string
    actionsExecuted: ExecutedAction[]
    undoWindowExpiresAt: string
  } | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const voiceNoteIdRef = useRef<string>('')

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [])

  // ── Start recording ──
  const startRecording = useCallback(async () => {
    try {
      setState('requesting-permission')
      setErrorMsg('')

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm',
      })

      chunksRef.current = []
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop())
      }

      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start(1000) // collect data every 1s

      setState('recording')
      setElapsedSec(0)
      timerRef.current = setInterval(() => {
        setElapsedSec((prev) => {
          if (prev >= MAX_DURATION_SEC - 1) {
            // Auto-stop at max duration
            stopRecording()
            return prev
          }
          return prev + 1
        })
      }, 1000)
    } catch (err) {
      setState('error')
      setErrorMsg(t.voice.errors.micPermission)
    }
  }, [t])

  // ── Stop recording ──
  const stopRecording = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    const mediaRecorder = mediaRecorderRef.current
    if (!mediaRecorder || mediaRecorder.state === 'inactive') return

    // Wait for the final data
    await new Promise<void>((resolve) => {
      const originalOnStop = mediaRecorder.onstop
      mediaRecorder.onstop = (e) => {
        if (originalOnStop && typeof originalOnStop === 'function') {
          originalOnStop.call(mediaRecorder, e)
        }
        resolve()
      }
      mediaRecorder.stop()
    })

    const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
    if (blob.size === 0) {
      setState('error')
      setErrorMsg(t.voice.recording.emptyRecording)
      return
    }

    await uploadAndProcess(blob)
  }, [t, bienId, elapsedSec])

  // ── Auto-start recording when mounted via AssistantFab ──
  useEffect(() => {
    if (autoStart && state === 'idle') {
      startRecording()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart])

  // ── Upload + process ──
  const uploadAndProcess = useCallback(async (blob: Blob) => {
    setState('uploading')

    try {
      // Step 1: Get presigned URL
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (import.meta.env.DEV) headers['X-Dev-Role'] = 'AGENT'

      const startRes = await fetch('/api/voice-notes/start', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          bienId: bienId || undefined,
          audioDurationSec: elapsedSec,
          consentFlag: true,
        }),
      })

      if (!startRes.ok) {
        const body = await startRes.json().catch(() => ({}))
        throw new Error(body.message || 'Failed to start voice note')
      }

      const { data } = (await startRes.json()) as StartResponse
      voiceNoteIdRef.current = data.voiceNoteId

      // Step 2: Upload audio via backend proxy to Garage
      const uploadRes = await fetch(data.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'audio/webm' },
        credentials: 'include',
      })
      if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status}`)

      // Step 3: Process via SSE
      setState('processing')
      setSteps([
        { name: 'transcribing', status: 'active' },
        { name: 'extracting', status: 'pending' },
        { name: 'executed', status: 'pending' },
      ])

      const processRes = await fetch(`/api/voice-notes/${data.voiceNoteId}/process`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!processRes.ok) {
        throw new Error('Processing failed')
      }

      // Read SSE stream
      const reader = processRes.body?.getReader()
      const decoder = new TextDecoder()

      if (reader) {
        let buffer = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const event: ProcessEvent = JSON.parse(line.slice(6))
              handleSSEEvent(event)
            } catch {
              // skip malformed lines
            }
          }
        }
      }
    } catch (err) {
      setState('error')
      setErrorMsg(err instanceof Error ? err.message : t.voice.errors.processingFailed)
    }
  }, [bienId, elapsedSec, t])

  // ── Handle SSE events ──
  const handleSSEEvent = useCallback((event: ProcessEvent) => {
    if (event.step) {
      setSteps((prev) =>
        prev.map((s) => {
          if (s.name === event.step) {
            return { ...s, status: (event.status as 'done' | 'active' | 'pending') || s.status, detail: event.detail || s.detail }
          }
          return s
        }),
      )
    }

    if (event.actionsExecuted && event.undoWindowExpiresAt) {
      setState('done')
      setUndoData({
        voiceNoteId: voiceNoteIdRef.current,
        actionsExecuted: event.actionsExecuted,
        undoWindowExpiresAt: event.undoWindowExpiresAt,
      })
    }

    if (event.error) {
      setState('error')
      setErrorMsg(event.error)
    }
  }, [])

  // ── Undo handler ──
  const handleUndo = useCallback(async (noteId: string) => {
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (import.meta.env.DEV) headers['X-Dev-Role'] = 'AGENT'

      await fetch(`/api/voice-notes/${noteId}/undo`, {
        method: 'POST',
        credentials: 'include',
        headers,
      })
      setUndoData(null)
      setState('idle')
      onComplete?.()
    } catch {
      // fail silently — window may have expired
    }
  }, [onComplete])

  const handleUndoExpire = useCallback(() => {
    setUndoData(null)
    setState('idle')
    onComplete?.()
  }, [onComplete])

  // ── Reset to idle ──
  const resetToIdle = useCallback(() => {
    setState('idle')
    setSteps([])
    setErrorMsg('')
    setUndoData(null)
    setElapsedSec(0)
  }, [])

  // ── FAB click handler ──
  const handleFabClick = useCallback(() => {
    if (state === 'idle' || state === 'done' || state === 'error') {
      startRecording()
    } else if (state === 'recording') {
      stopRecording()
    }
  }, [state, startRecording, stopRecording])

  // ── Render ──
  const isRecording = state === 'recording'
  const isProcessing = state === 'uploading' || state === 'processing'
  const showProcessing = state === 'processing' || state === 'done'

  // Reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches

  return (
    <>
      {/* ── Recording overlay ── */}
      {isRecording && (
        <div
          className="fixed inset-0 z-[9990] flex flex-col items-center justify-center gap-6 p-6"
          style={{ background: 'var(--color-background)' }}
        >
          <p
            className="text-xs font-medium uppercase tracking-wider"
            style={{ color: 'var(--color-error)' }}
          >
            {t.voice.recording.label}
          </p>

          <p
            className="text-4xl font-medium"
            style={{ color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}
          >
            {formatTimer(elapsedSec)}
          </p>

          {/* Waveform */}
          <div className="flex items-center gap-1" style={{ height: 40 }} aria-hidden="true">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className="w-1 rounded-full"
                style={{
                  background: 'var(--color-primary)',
                  height: prefersReducedMotion ? '16px' : undefined,
                  animation: prefersReducedMotion
                    ? 'none'
                    : `voice-wave 0.8s ease-in-out ${i * 0.1}s infinite`,
                }}
              />
            ))}
          </div>

          {bienId && (
            <div
              className="w-full max-w-xs rounded-xl p-3 text-center"
              style={{ background: 'var(--color-surface-raised)' }}
            >
              <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                {t.voice.recording.associatedProperty}
              </p>
            </div>
          )}

          {/* Stop button */}
          <button
            onClick={stopRecording}
            className="flex h-14 w-14 items-center justify-center rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
            style={{
              background: 'var(--color-error)',
              minHeight: '56px',
              minWidth: '56px',
            }}
            aria-label={t.voice.recording.stopAriaLabel}
          >
            <Square size={18} fill="var(--color-text-inverse)" style={{ color: 'var(--color-text-inverse)' }} />
          </button>

          <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {t.voice.recording.tapToStop}
          </p>
        </div>
      )}

      {/* ── Processing overlay ── */}
      {showProcessing && (
        <div
          className="fixed inset-0 z-[9990] flex flex-col gap-4 overflow-auto p-5"
          style={{ background: 'var(--color-background)' }}
        >
          <div className="mx-auto w-full max-w-md">
            <ProcessingSteps steps={steps} />

            {state === 'done' && (
              <div className="mt-4">
                <button
                  onClick={resetToIdle}
                  className="w-full rounded-lg py-3 text-center text-sm font-medium transition-colors"
                  style={{
                    background: 'var(--color-primary)',
                    color: 'var(--color-text-inverse)',
                  }}
                >
                  {t.voice.inline.close}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Error banner ── */}
      {state === 'error' && (
        <div
          className="fixed bottom-24 left-4 right-4 z-[9998] mx-auto max-w-md"
        >
          <div
            className="flex items-center gap-3 rounded-xl px-4 py-3"
            style={{
              background: 'var(--color-status-error-light)',
              border: '1px solid var(--color-status-error)',
            }}
            role="alert"
          >
            <AlertCircle size={18} style={{ color: 'var(--color-status-error)' }} aria-hidden="true" />
            <p className="flex-1 text-sm" style={{ color: 'var(--color-status-error)' }}>
              {errorMsg || t.voice.errors.processingFailed}
            </p>
            <button
              onClick={resetToIdle}
              className="shrink-0 text-sm font-medium underline"
              style={{ color: 'var(--color-status-error)', minHeight: '44px', minWidth: '44px' }}
            >
              {t.voice.inline.close}
            </button>
          </div>
        </div>
      )}

      {/* ── Undo toast ── */}
      {undoData && (
        <UndoToast
          voiceNoteId={undoData.voiceNoteId}
          actionsExecuted={undoData.actionsExecuted}
          undoWindowExpiresAt={undoData.undoWindowExpiresAt}
          onUndo={handleUndo}
          onExpire={handleUndoExpire}
        />
      )}

      {/* ── FAB button ── */}
      <button
        onClick={handleFabClick}
        disabled={isProcessing}
        className="fixed bottom-6 right-6 z-[9995] flex items-center justify-center rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        style={{
          width: 56,
          height: 56,
          minWidth: 56,
          minHeight: 56,
          background: isRecording ? 'var(--color-error)' : 'var(--color-primary)',
          color: 'var(--color-text-inverse)',
          boxShadow: 'var(--shadow-elevated)',
          opacity: isProcessing ? 0.5 : 1,
          cursor: isProcessing ? 'not-allowed' : 'pointer',
          animation: isRecording && !prefersReducedMotion ? 'voice-pulse 1.2s ease-in-out infinite' : 'none',
        }}
        aria-label={
          isRecording
            ? t.voice.recording.stopAriaLabel
            : isProcessing
              ? t.voice.fab.processing
              : t.voice.recording.startAriaLabel
        }
      >
        {isProcessing ? (
          <Loader2 size={22} className="animate-spin" aria-hidden="true" />
        ) : (
          <Mic size={22} aria-hidden="true" />
        )}
      </button>

      {/* ── CSS keyframes for waveform animation ── */}
      <style>{`
        @keyframes voice-wave {
          0%, 100% { height: 6px; }
          50% { height: 32px; }
        }
        @keyframes voice-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.75; }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes voice-wave { 0%, 100% { height: 16px; } }
          @keyframes voice-pulse { 0%, 100% { opacity: 1; } }
        }
      `}</style>
    </>
  )
}
